import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useLastVideoState } from '~/composables/shorts/useLastVideoState';
import { useShortsEntryContext } from '~/composables/shorts/useShortsEntryContext';
import { shortsFiltersToListingQuery } from '~/composables/useShortsFilters/featuredVideosFilterRules';

import { blessSharedVideoElement } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

export const SHORTS_ENTRY_SOURCE = {
	CLUSTER: 'cluster'
} as const;

export type ShortsEntrySource =
	(typeof SHORTS_ENTRY_SOURCE)[keyof typeof SHORTS_ENTRY_SOURCE];

const SHORTS_ENTRY_SOURCE_QUERY_KEY = 'entrySource';

export interface OpenShortsFeedEntryContext {
	items: ShortVideo[];
	filters: ShortsFilters;
	page: number;
	perPage?: number;
	hasMore: boolean;
}

interface OpenShortsFeedParams {
	commercialId: string;
	clickedIndex: number;
	entryContext?: OpenShortsFeedEntryContext | null;
	queryFilters?: ShortsFilters;
	queryParams?: Record<string, string>;
	entrySource?: ShortsEntrySource;
}

function withEntrySource(
	query: Record<string, string>,
	entrySource?: ShortsEntrySource
): Record<string, string> {
	if (!entrySource) return query;

	return {
		...query,
		[SHORTS_ENTRY_SOURCE_QUERY_KEY]: entrySource
	};
}

export function isShortsEntrySource(
	query: Record<string, unknown>,
	entrySource: ShortsEntrySource
): boolean {
	const value = query[SHORTS_ENTRY_SOURCE_QUERY_KEY];
	return Array.isArray(value)
		? value[0] === entrySource
		: value === entrySource;
}

export function buildShortsFeedDestination(
	commercialId: string,
	query: Record<string, string> = {},
	entrySource?: ShortsEntrySource
): string {
	const search = new URLSearchParams(
		withEntrySource(query, entrySource)
	).toString();
	return `/shorts/${commercialId}${search ? `?${search}` : ''}`;
}

export function useOpenShortsFeed() {
	const lastVideoState = useLastVideoState();
	const shortsEntryContext = useShortsEntryContext();

	function openShortsFeed({
		commercialId,
		clickedIndex,
		entryContext,
		queryFilters,
		queryParams,
		entrySource
	}: OpenShortsFeedParams): void {
		const path = `/shorts/${commercialId}`;

		lastVideoState.save(commercialId, clickedIndex);
		blessSharedVideoElement();

		if (entryContext) {
			shortsEntryContext.value = entryContext;
		}

		if (queryParams) {
			navigateTo({ path, query: withEntrySource(queryParams, entrySource) });
			return;
		}

		const filters = queryFilters ?? entryContext?.filters;
		if (!filters) {
			if (entrySource) {
				navigateTo({ path, query: withEntrySource({}, entrySource) });
			} else {
				navigateTo(path);
			}
			return;
		}

		navigateTo({
			path,
			query: withEntrySource(shortsFiltersToListingQuery(filters), entrySource)
		});
	}

	return { openShortsFeed };
}
